/**
 * CrossTeamShare.jsx
 * Shared utilities for inter-portal file sharing, access control, and cross-team linking.
 * Used by MissionPlanningPortal, EmbeddedSystemPortal, SystemEngineeringPortal.
 */
import { useState, useEffect } from 'react';
import {
  collection, doc, setDoc, updateDoc, onSnapshot, query, where, serverTimestamp,
} from 'firebase/firestore';
import { exportReportPDF } from '../utils/reportPdfExport';

// ── Portal registry ───────────────────────────────────────────────────────────
export const PORTAL_INFO = {
  mp:       { label:'Mission Planning',    icon:'🛰', collection:'mission_planning_posts', color:'#6366f1' },
  embedded: { label:'Embedded Systems',    icon:'🔌', collection:'embedded_posts',         color:'#f59e0b' },
  system:   { label:'Systems Engineering', icon:'⚙',  collection:'system_posts',           color:'#10b981' },
  ops:      { label:'Operations',          icon:'🏛', collection:'',                       color:'#e11d48' },
  admin:    { label:'Administration',      icon:'⭐', collection:'',                       color:'#ef4444' },
};

// All portals available for portal-level cross-team sharing
export const ALL_PORTAL_SHARING = [
  { id:'mp',           label:'Mission Planning',   color:'#6366f1', icon:'🛰'  },
  { id:'embedded',     label:'Embedded Systems',   color:'#f59e0b', icon:'🔌'  },
  { id:'system',       label:'Systems Eng',        color:'#10b981', icon:'⚙'   },
  { id:'ops',          label:'Operations',         color:'#e11d48', icon:'🏛'  },
  { id:'pe',           label:'Prompt Engineering', color:'#8b5cf6', icon:'🧠'  },
  { id:'obc',          label:'OBC Development',    color:'#00FFB2', icon:'🛰️' },
  { id:'experimental', label:'Experimental',       color:'#06b6d4', icon:'🧪'  },
  { id:'finance',      label:'Finance',            color:'#10b981', icon:'💰'  },
];

// ══════════════════════════════════════════════════════════════════════════════
//  CROSS-TEAM PORTAL SELECTOR — simple pill selector for portal-level sharing
//  Props: currentPortalId, selectedPortals (string[]), onChange fn, C (theme tokens)
// ══════════════════════════════════════════════════════════════════════════════
export function CrossTeamPortalSelector({ currentPortalId, selectedPortals = [], onChange, C = {} }) {
  const others = ALL_PORTAL_SHARING.filter(p => p.id !== currentPortalId);
  const border = C.border || '#1e2636';
  const dim    = C.dim    || '#445566';
  const bg     = C.bg     || '#0b0e12';
  const mono   = C.mono   || "'Share Tech Mono', monospace";
  const accent = C.accent || '#10b981';

  return (
    <div style={{ marginTop:12, padding:'12px 14px', background: C.panel2 || bg,
      border:`0.5px solid ${border}`, borderRadius:8 }}>
      <div style={{ fontSize:9, fontFamily:mono, color:dim, letterSpacing:'0.08em', marginBottom:8 }}>
        SHARE WITH TEAMS (optional)
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {others.map(p => {
          const sel = selectedPortals.includes(p.id);
          return (
            <div key={p.id} onClick={() => onChange(
              sel ? selectedPortals.filter(id => id !== p.id) : [...selectedPortals, p.id]
            )} style={{
              display:'flex', alignItems:'center', gap:5,
              padding:'5px 10px', borderRadius:6, cursor:'pointer',
              border:`0.5px solid ${sel ? p.color + '50' : border}`,
              background: sel ? p.color + '15' : 'transparent',
              transition:'all 0.15s',
            }}>
              <span style={{ width:6, height:6, borderRadius:'50%',
                background: sel ? p.color : dim, flexShrink:0 }} />
              <span style={{ fontSize:11, color: sel ? p.color : dim,
                fontWeight: sel ? 500 : 400, fontFamily:mono }}>
                {p.label}
              </span>
              {sel && <span style={{ fontSize:9, color:p.color }}>✓</span>}
            </div>
          );
        })}
      </div>
      {selectedPortals.length > 0 && (
        <div style={{ marginTop:8, fontSize:10, color:dim,
          display:'flex', alignItems:'center', gap:5 }}>
          <span style={{ color:accent }}>ℹ</span>
          Members of {selectedPortals.length} team{selectedPortals.length > 1 ? 's' : ''} will see
          this report in their feed. They can read and add thread replies.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CROSS-TEAM LINKER — embedded inside UploadModal of each portal
//  Props: db, currentPortal ('mp'|'embedded'|'system'), links [], onChange fn, accent color
// ══════════════════════════════════════════════════════════════════════════════
export function CrossTeamLinker({ db, currentPortal, links, onChange, accent, themeBg, themePanel, themeBorder, themeText, themeBright, themeDim, themeMono }) {
  const [members,   setMembers]   = useState([]);
  const [selPortal, setSelPortal] = useState('');
  const [selMember, setSelMember] = useState('');
  const [note,      setNote]      = useState('');

  const otherPortals = Object.keys(PORTAL_INFO).filter(p => p !== currentPortal);

  // Resolve theme colors — use portal-supplied values when available, fall back to SH
  const T = {
    bg:     themeBg     || SH.bg,
    panel:  themePanel  || SH.panel,
    border: themeBorder || SH.border,
    text:   themeText   || SH.text,
    bright: themeBright || SH.bright,
    dim:    themeDim    || SH.dim,
    mono:   themeMono   || SH.mono,
  };

  useEffect(() => {
    if (!db || !otherPortals.length) return;
    const q = query(collection(db, 'team_members'), where('portal', 'in', otherPortals));
    const unsub = onSnapshot(q, snap => {
      setMembers(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    }, () => {});
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, currentPortal]);

  const portalMembers = selPortal ? members.filter(m => m.portal === selPortal) : [];
  const col = accent || SH.green;

  function addLink() {
    if (!selPortal) return;
    const pi = PORTAL_INFO[selPortal];
    const isTeam     = !selMember || selMember === '__team';
    const memberName = isTeam ? `${pi.label} Team` : selMember;
    const memberKey  = isTeam ? `__team|${selPortal}` : `${selMember}|${selPortal}`;
    if ((links||[]).some(l => l.memberKey === memberKey)) return;
    const linkedUid  = isTeam ? null : (members.find(m => m.name === selMember && m.portal === selPortal)?.uid || null);
    onChange([...(links||[]), {
      portal: selPortal, memberKey, memberName, uid: linkedUid,
      portalLabel: pi.label, portalIcon: pi.icon, portalColor: pi.color,
      note: note.trim(),
    }]);
    setSelMember(''); setNote('');
  }

  function removeLink(memberKey) {
    onChange((links||[]).filter(l => l.memberKey !== memberKey));
  }

  return (
    <div>
      <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, letterSpacing:2, marginBottom:10 }}>
        CROSS-TEAM LINKS
      </div>

      {/* Active links */}
      {(links||[]).length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
          {(links||[]).map(l => (
            <div key={l.memberKey} style={{ display:'flex', alignItems:'center', gap:5,
              background:`${l.portalColor}15`, border:`1px solid ${l.portalColor}55`,
              padding:'3px 8px 3px 10px', fontFamily:T.mono, fontSize:10, borderRadius:2 }}>
              <span style={{ color:l.portalColor }}>{l.portalIcon} {l.memberName}</span>
              {l.note && <span style={{ color:T.dim, fontSize:9 }}> — {l.note}</span>}
              <button type="button" onClick={() => removeLink(l.memberKey)}
                style={{ background:'none', border:'none', color:T.dim, cursor:'pointer', fontSize:14, lineHeight:1, padding:'0 2px', marginLeft:2 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Add link row */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:6 }}>
        <select value={selPortal} onChange={e => { setSelPortal(e.target.value); setSelMember(''); }}
          style={{ flex:1, minWidth:140, background:T.bg, border:`1px solid ${T.border}`,
            color: selPortal ? T.bright : T.dim, fontFamily:T.mono, fontSize:11, padding:'8px 10px' }}>
          <option value="">— select team —</option>
          {otherPortals.map(p => {
            const pi = PORTAL_INFO[p];
            return <option key={p} value={p}>{pi.icon} {pi.label}</option>;
          })}
        </select>

        {selPortal && (
          <select value={selMember} onChange={e => setSelMember(e.target.value)}
            style={{ flex:1, minWidth:140, background:T.bg, border:`1px solid ${T.border}`,
              color: selMember ? T.bright : T.dim, fontFamily:T.mono, fontSize:11, padding:'8px 10px' }}>
            <option value="">— member (optional) —</option>
            <option value="__team">Entire team</option>
            {portalMembers.map(m => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
            {portalMembers.length === 0 && (
              <option disabled value="">No members logged in yet</option>
            )}
          </select>
        )}
      </div>

      {selPortal && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          <input value={note} onChange={e => setNote(e.target.value)}
            placeholder="Context note (e.g. PCB dimensions needed, fits within 100×80mm)"
            style={{ flex:1, minWidth:180, background:T.bg, border:`1px solid ${T.border}`,
              color:T.text, fontFamily:T.mono, fontSize:10, padding:'7px 10px' }} />
          <button type="button" onClick={addLink} style={{
            padding:'7px 16px', fontFamily:T.mono, fontSize:9, cursor:'pointer', whiteSpace:'nowrap',
            background:`${col}18`, border:`1px solid ${col}`, color:col }}>
            + ADD LINK
          </button>
        </div>
      )}

      {!(links||[]).length && !selPortal && (
        <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, lineHeight:1.7 }}>
          Link this report to another team or a specific team member.<br/>
          They will see it highlighted in their cross-team feed.
        </div>
      )}
    </div>
  );
}

// Register team member in Firestore when they log in — includes uid for edit grants
export async function registerMember(db, name, portal, uid) {
  const docId = `${name.toLowerCase().replace(/[^a-z0-9]/g,'_')}_${portal}`;
  await setDoc(doc(db, 'team_members', docId), {
    name, portal, lastSeen: serverTimestamp(), ...(uid ? { uid } : {}),
  }, { merge: true });
}

// Hook: real-time feed of posts shared/linked with the current user from other portals
// Matches both exact user key ("Name|portal") and team-level key ("__team|portal")
// Only subscribes to portals that have a Firestore collection defined.
export function useSharedWithMe(db, userName, myPortal) {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!userName || !myPortal || !db) return;
    const userKey = `${userName}|${myPortal}`;
    const teamKey = `__team|${myPortal}`;
    // Only portals with a real collection (ops/admin have empty string — skip)
    const others = Object.keys(PORTAL_INFO)
      .filter(p => p !== myPortal && PORTAL_INFO[p].collection);
    const parts = {};

    const unsubs = others.flatMap(portal => {
      const colName = PORTAL_INFO[portal].collection;
      const merge = (key) => onSnapshot(
        query(collection(db, colName), where('sharedWith', 'array-contains', key)),
        snap => {
          parts[`${portal}__${key}`] = snap.docs.map(d => ({ id: d.id, _fromPortal: portal, ...d.data() }));
          const all = Object.values(parts).flat();
          const seen = new Set();
          setPosts(all.filter(q => {
            const k = q.id + q._fromPortal;
            if (seen.has(k)) return false;
            seen.add(k); return true;
          }));
        }, () => {}
      );
      return [merge(userKey), merge(teamKey)];
    });
    return () => unsubs.forEach(u => u());
  }, [db, userName, myPortal]);
  return posts;
}

// ── Shared UI theme (neutral dark, works across all portals) ─────────────────
const SH = {
  bg:'#0b0e12', panel:'#111620', border:'#1e2636',
  text:'#b8ccd8', bright:'#dde9f0', dim:'#445566',
  green:'#10b981', red:'#f43f5e', yellow:'#f59e0b',
  mono:"'Share Tech Mono', monospace", sans:"'Inter', system-ui, sans-serif",
};

const fmtDate = ts => {
  if (!ts?.toDate) return ts ? new Date(ts).toLocaleDateString() : '—';
  return ts.toDate().toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
};
const fmtSize = b => !b ? '' : b > 1048576 ? (b/1048576).toFixed(1)+' MB' : (b/1024).toFixed(0)+' KB';
const isImg  = n => /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(n||'');
const isVid  = n => /\.(mp4|webm|mov|avi|mkv|m4v)$/i.test(n||'');
const isAud  = n => /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(n||'');
const isPDF  = n => /\.pdf$/i.test(n||'');
const fIcon  = n => isImg(n)?'🖼':isVid(n)?'🎥':isAud(n)?'🎵':isPDF(n)?'📄':'📎';

// ══════════════════════════════════════════════════════════════════════════════
//  SHARE PANEL — shown in post admin/share tab to manage access
// ══════════════════════════════════════════════════════════════════════════════
export function SharePanel({ db, post, postCollection, isAdmin, isAuthor }) {
  const [members,  setMembers]  = useState([]);
  const [selected, setSelected] = useState('');
  const [busy,     setBusy]     = useState('');
  const [dlEnabled,setDlEnabled]= useState(post.downloadsEnabled !== false);

  // Determine which portal owns this collection
  const ownerPortal = Object.entries(PORTAL_INFO).find(([,v]) => v.collection === postCollection)?.[0] || '';
  const otherPortals = Object.keys(PORTAL_INFO).filter(p => p !== ownerPortal);

  // Fetch team members from other portals
  useEffect(() => {
    if (!otherPortals.length || !db) return;
    const q = query(collection(db, 'team_members'), where('portal', 'in', otherPortals));
    const unsub = onSnapshot(q, snap => {
      setMembers(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    }, () => {});
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, ownerPortal]);

  const current = post.sharedWith || [];
  const available = members.filter(m => !current.includes(`${m.name}|${m.portal}`));

  async function addShare() {
    if (!selected || current.includes(selected)) return;
    setBusy('add');
    await updateDoc(doc(db, postCollection, post.id), { sharedWith: [...current, selected] });
    setSelected(''); setBusy('');
  }

  async function removeShare(key) {
    setBusy(key);
    await updateDoc(doc(db, postCollection, post.id), { sharedWith: current.filter(s => s !== key) });
    setBusy('');
  }

  async function toggleDownloads() {
    const v = !dlEnabled;
    setDlEnabled(v);
    await updateDoc(doc(db, postCollection, post.id), { downloadsEnabled: v });
  }

  if (!isAdmin && !isAuthor) return null;

  return (
    <div>
      <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim, letterSpacing:2, marginBottom:14 }}>
        SHARE WITH TEAM
      </div>

      {/* Current shared list */}
      {current.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim, marginBottom:8 }}>CURRENTLY SHARED WITH</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {current.map(key => {
              const [name, portal] = key.split('|');
              const pi = PORTAL_INFO[portal];
              return (
                <div key={key} style={{ display:'flex', alignItems:'center', gap:5,
                  background:`${pi?.color||'#888'}15`, border:`1px solid ${pi?.color||'#888'}44`,
                  padding:'3px 8px 3px 10px', fontFamily:SH.mono, fontSize:10 }}>
                  <span style={{ color: pi?.color||'#888' }}>{pi?.icon} {name}</span>
                  <span style={{ color:SH.dim, fontSize:9 }}>({pi?.label})</span>
                  {(isAdmin || isAuthor) && (
                    <button onClick={() => removeShare(key)} disabled={busy===key}
                      style={{ background:'none', border:'none', color:SH.dim, cursor:'pointer',
                        fontSize:13, lineHeight:1, padding:'0 2px', marginLeft:2 }}>
                      {busy===key ? '…' : '×'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add member */}
      <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim, marginBottom:8 }}>ADD TEAM MEMBER</div>
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <select value={selected} onChange={e => setSelected(e.target.value)}
          style={{ flex:1, minWidth:200, background:SH.bg, border:`1px solid ${SH.border}`,
            color: selected ? SH.bright : SH.dim, fontFamily:SH.mono, fontSize:11, padding:'8px 10px' }}>
          <option value="">— select a team member —</option>
          {otherPortals.map(portal => {
            const grp = available.filter(m => m.portal === portal);
            if (!grp.length) return null;
            const pi = PORTAL_INFO[portal];
            return (
              <optgroup key={portal} label={`${pi.icon} ${pi.label}`}>
                {grp.map(m => (
                  <option key={m.id} value={`${m.name}|${m.portal}`}>{m.name}</option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <button onClick={addShare} disabled={!selected || busy==='add'} style={{
          padding:'8px 18px', fontFamily:SH.mono, fontSize:10, cursor: !selected||busy==='add' ? 'not-allowed' : 'pointer',
          background: !selected ? 'transparent' : `${SH.green}18`,
          border:`1px solid ${!selected ? SH.border : SH.green}`,
          color: !selected ? SH.dim : SH.green, whiteSpace:'nowrap' }}>
          {busy==='add' ? 'ADDING…' : '+ SHARE'}
        </button>
      </div>

      {available.length === 0 && members.length === 0 && (
        <div style={{ fontFamily:SH.mono, fontSize:10, color:SH.dim, padding:'10px 0', marginBottom:12 }}>
          No team members from other portals have logged in yet. They will appear here once they log in to their respective portals.
        </div>
      )}

      {/* Downloads toggle */}
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
        background:SH.panel, border:`1px solid ${SH.border}` }}>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:SH.mono, fontSize:10, color:SH.bright }}>ALLOW DOWNLOADS</div>
          <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim, marginTop:3 }}>
            {dlEnabled ? 'Shared members can download files from this report' : 'File downloads disabled for shared team members'}
          </div>
        </div>
        <button onClick={toggleDownloads} style={{
          padding:'7px 16px', fontFamily:SH.mono, fontSize:10, cursor:'pointer', whiteSpace:'nowrap',
          background: dlEnabled ? `${SH.green}18` : `${SH.red}12`,
          border:`1px solid ${dlEnabled ? SH.green : SH.red}`,
          color: dlEnabled ? SH.green : SH.red }}>
          {dlEnabled ? '✓ ENABLED' : '✗ DISABLED'}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  SHARED POST CARD — compact card in the "SHARED" feed tab
// ══════════════════════════════════════════════════════════════════════════════
export function SharedPostCard({ post, onOpen }) {
  const pi = PORTAL_INFO[post._fromPortal] || {};
  const [hov, setHov] = useState(false);
  const excerpt = (post.sections||[]).find(s => s.type==='paragraph')?.text || '';

  return (
    <div onClick={() => onOpen(post)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background:SH.panel, border:`1px solid ${hov ? pi.color+'55' : SH.border}`,
        cursor:'pointer', transition:'border-color 0.15s, transform 0.15s',
        transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? `0 8px 24px ${pi.color}18` : 'none',
        display:'flex', flexDirection:'column', position:'relative' }}>
      {/* Cover */}
      <div style={{ height:160, position:'relative', overflow:'hidden', flexShrink:0 }}>
        {post.coverUrl ? (
          <>
            <img src={post.coverUrl} alt={post.title}
              style={{ width:'100%', height:'100%', objectFit:'cover', opacity:0.55 }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,#0b0e1200 0%,#0b0e12ee 100%)' }} />
            <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'8px 12px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#fff', lineHeight:1.35,
                display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                {post.title}
              </div>
            </div>
          </>
        ) : (
          <div style={{ width:'100%', height:'100%', background:'#fff', padding:'12px 14px', overflow:'hidden', position:'relative' }}>
            <div style={{ borderBottom:`2px solid ${pi.color||'#888'}`, paddingBottom:4, marginBottom:7 }}>
              <div style={{ fontSize:6, color:'#999', letterSpacing:1.5, fontFamily:'monospace', textTransform:'uppercase' }}>Spacemancraft Pvt Ltd</div>
              <div style={{ fontSize:7, color:'#777', fontFamily:'Arial,sans-serif' }}>{pi.icon} {pi.label}</div>
            </div>
            <div style={{ fontSize:10, fontWeight:700, color: pi.color||'#333', lineHeight:1.3, fontFamily:'serif',
              marginBottom:4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
              {post.title}
            </div>
            {excerpt && (
              <div style={{ fontSize:7.5, color:'#444', lineHeight:1.5, fontFamily:'serif',
                display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                {excerpt}
              </div>
            )}
            <div style={{ position:'absolute', bottom:0, left:0, right:0, height:36,
              background:'linear-gradient(to bottom, transparent, #ffffffe0)' }} />
          </div>
        )}
        {/* Portal source badge */}
        <div style={{ position:'absolute', top:7, right:7,
          background:`${pi.color||'#888'}18`, border:`1px solid ${pi.color||'#888'}44`,
          padding:'2px 8px', fontFamily:SH.mono, fontSize:9, color: pi.color||'#888' }}>
          {pi.icon} {pi.label}
        </div>
        {/* Download lock indicator */}
        {post.downloadsEnabled === false && (
          <div style={{ position:'absolute', bottom:7, left:7,
            background:'rgba(244,63,94,0.85)', padding:'1px 7px',
            fontFamily:SH.mono, fontSize:8, color:'#fff' }}>🔒 NO DOWNLOAD</div>
        )}
      </div>
      {/* Footer */}
      <div style={{ padding:'9px 12px', borderTop:`1px solid ${SH.border}` }}>
        <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim }}>
          by {post.uploadedBy} · {fmtDate(post.uploadedAt)}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  SHARED POST VIEWER — full report view for a cross-portal post
// ══════════════════════════════════════════════════════════════════════════════
export function SharedPostViewer({ post, onClose, userId }) {
  const pi = PORTAL_INFO[post._fromPortal] || {};
  const [tab, setTab] = useState('report');
  const [exporting, setExporting] = useState(false);
  let sectionNum = 0, figNum = 0;

  async function handleExportPDF() {
    setExporting(true);
    try {
      await exportReportPDF(post, pi, userId);
    } catch (e) {
      console.error('[PDF export]', e);
    } finally {
      setExporting(false);
    }
  }

  const canDownload = post.downloadsEnabled !== false;

  const attachments = [
    ...(post.files || []),
    ...(post.sections||[]).filter(s => s.type==='attachment' && s.url).map(s=>({url:s.url,name:s.name,size:s.size})),
  ].filter((f,i,a) => a.findIndex(x=>x.url===f.url)===i);

  const secStyle = lvl => ({
    fontFamily:'Arial,sans-serif',
    fontSize: lvl===1 ? '14pt' : lvl===2 ? '11.5pt' : '10.5pt',
    fontWeight:700, color: lvl<=2 ? (pi.color||'#1a3a8a') : '#333',
    margin: lvl===1 ? '20px 0 8px' : lvl===2 ? '14px 0 5px' : '10px 0 4px',
    lineHeight:1.35,
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'#000000cc', zIndex:450,
      display:'flex', flexDirection:'column', overflowY:'auto' }}>
      <div style={{ background:SH.panel, flex:1, display:'flex', flexDirection:'column',
        width:'100%', maxWidth:900, margin:'0 auto', alignSelf:'stretch' }}>

        {/* Top bar */}
        <div style={{ background:SH.bg, borderBottom:`1px solid ${SH.border}`,
          padding:'10px 20px', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap',
          position:'sticky', top:0, zIndex:10 }}>
          <button onClick={onClose} style={{ background:'none', border:'none', color:SH.dim,
            cursor:'pointer', fontFamily:SH.mono, fontSize:11, letterSpacing:1, flexShrink:0 }}>← BACK</button>
          <div style={{ display:'flex', gap:6, flex:1, flexWrap:'wrap' }}>
            {[['report','REPORT'], ['files',`FILES (${attachments.length})`]].map(([v,l]) => (
              <button key={v} onClick={() => setTab(v)} style={{
                padding:'5px 11px', fontFamily:SH.mono, fontSize:9, cursor:'pointer',
                background: tab===v ? `${pi.color||'#888'}20` : 'transparent',
                border:`1px solid ${tab===v ? pi.color||'#888' : SH.border}`,
                color: tab===v ? pi.color||'#888' : SH.dim, letterSpacing:1, whiteSpace:'nowrap' }}>{l}</button>
            ))}
          </div>
          <button onClick={handleExportPDF} disabled={exporting} style={{
            fontFamily:SH.mono, fontSize:9, letterSpacing:1, cursor: exporting ? 'default' : 'pointer',
            padding:'5px 12px', background:'#0e9cc020', border:'1px solid #0e9cc0',
            color:'#0e9cc0', whiteSpace:'nowrap', flexShrink:0, opacity: exporting ? 0.6 : 1 }}>
            {exporting ? 'EXPORTING…' : '↓ PDF'}
          </button>
          <div style={{ fontFamily:SH.mono, fontSize:9, color: pi.color||'#888',
            background:`${pi.color||'#888'}15`, border:`1px solid ${pi.color||'#888'}44`,
            padding:'3px 10px', whiteSpace:'nowrap' }}>
            {pi.icon} FROM {pi.label?.toUpperCase()}
          </div>
        </div>

        {/* Report tab */}
        {tab === 'report' && (
          <div style={{ flex:1, overflowY:'auto', background:'#e8eaf0', padding:'20px 12px' }}>
            <div style={{ background:'#fff', color:'#111', padding:'36px 48px',
              fontFamily:'Times New Roman,Times,serif', fontSize:'11pt', lineHeight:1.65,
              maxWidth:820, margin:'0 auto' }}>
              <div style={{ borderBottom:`2.5px solid ${pi.color||'#1a3a8a'}`, paddingBottom:12, marginBottom:24 }}>
                <div style={{ fontFamily:'Arial,sans-serif', fontSize:'8pt', color:'#999', letterSpacing:2, textTransform:'uppercase', marginBottom:2 }}>Spacemancraft Pvt Ltd</div>
                <div style={{ fontFamily:'Arial,sans-serif', fontSize:'9pt', color:'#777', marginBottom:8 }}>{pi.icon} {pi.label} Report · Shared Document</div>
                <div style={{ fontFamily:'Arial,sans-serif', fontSize:'19pt', fontWeight:700, color:pi.color||'#1a3a8a', lineHeight:1.25, marginBottom:7 }}>{post.title}</div>
                <div style={{ fontFamily:'Arial,sans-serif', fontSize:'9pt', color:'#666', display:'flex', flexWrap:'wrap', gap:'0 16px' }}>
                  <span>Author: <b>{post.uploadedBy}</b></span>
                  <span>Date: {fmtDate(post.uploadedAt)}</span>
                </div>
              </div>

              {(post.sections||[]).map((sec, i) => {
                switch (sec.type) {
                  case 'heading': {
                    const lvl = sec.level||1;
                    if (lvl===1) sectionNum++;
                    return <div key={i} style={secStyle(lvl)}>{lvl===1?`${sectionNum}. `:''}{sec.text}</div>;
                  }
                  case 'paragraph':
                    return <p key={i} style={{ margin:'0 0 10px', textAlign:'justify', lineHeight:1.7 }}>{sec.text}</p>;
                  case 'bullets':
                    return <ul key={i} style={{ margin:'6px 0 12px', paddingLeft:26, lineHeight:1.65 }}>
                      {(sec.items||[]).filter(Boolean).map((it,j) => <li key={j} style={{ marginBottom:3 }}>{it}</li>)}
                    </ul>;
                  case 'code':
                    return (
                      <div key={i} style={{ margin:'10px 0' }}>
                        {sec.language && <div style={{ fontFamily:'Arial,sans-serif', fontSize:'8pt', color:'#999', background:'#f5f5f8', padding:'3px 12px', border:'1px solid #dde', borderBottom:'none', letterSpacing:1 }}>{sec.language.toUpperCase()}</div>}
                        <pre style={{ background:'#f5f5f8', border:'1px solid #dde', padding:'10px 13px', margin:0, fontFamily:"'Courier New',monospace", fontSize:'9pt', lineHeight:1.5, overflowX:'auto', whiteSpace:'pre-wrap' }}>{sec.text}</pre>
                      </div>
                    );
                  case 'graph':
                  case 'image': {
                    figNum++;
                    return (
                      <div key={i} style={{ margin:'20px 0', textAlign:'center' }}>
                        {sec.url && isImg(sec.name) && <img src={sec.url} alt={sec.caption||'figure'} style={{ width:'100%', maxWidth:'100%', display:'block', border:'1px solid #e0e0e0', margin:'0 auto' }} />}
                        {sec.url && isVid(sec.name) && <video src={sec.url} controls style={{ width:'100%', maxHeight:340, display:'block', margin:'0 auto' }} />}
                        {sec.url && isAud(sec.name) && <audio src={sec.url} controls style={{ width:'100%', marginBottom:4 }} />}
                        {sec.url && isPDF(sec.name) && (
                          <div style={{ position:'relative', display:'inline-block', width:'100%' }}>
                            {sec.thumbnailUrl ? <img src={sec.thumbnailUrl} alt={sec.caption||'PDF'} style={{ width:'100%', display:'block', border:'1px solid #e0e0e0', margin:'0 auto' }} /> : <div style={{ height:120, background:'#f5f5f5', border:'1px dashed #ccc', display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa' }}>📄 PDF</div>}
                            {sec.numPages && <div style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,0.6)', color:'#fff', fontFamily:'Arial,sans-serif', fontSize:'8pt', padding:'2px 7px', borderRadius:3 }}>{sec.numPages} pages</div>}
                            <div style={{ marginTop:6 }}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:pi.color||'#1a3a8a', fontFamily:'Arial,sans-serif', fontSize:'9pt', textDecoration:'underline' }}>📄 Open full PDF →</a></div>
                          </div>
                        )}
                        {!sec.url && <div style={{ height:120, background:'#f5f5f5', border:'1px dashed #ccc', display:'flex', alignItems:'center', justifyContent:'center', color:'#bbb' }}>[No file]</div>}
                        {sec.caption && <div style={{ fontFamily:'Arial,sans-serif', fontSize:'10pt', fontWeight:700, color:'#222', margin:'8px 0 4px' }}>Fig {figNum} – {sec.caption}</div>}
                        {sec.description && <p style={{ fontFamily:'Arial,sans-serif', fontSize:'10pt', color:'#444', textAlign:'left', margin:'4px 0 12px', lineHeight:1.6 }}>{sec.description}</p>}
                      </div>
                    );
                  }
                  case 'attachment':
                    return sec.url ? (
                      <div key={i} style={{ fontFamily:'Arial,sans-serif', fontSize:'10pt', color:'#555', margin:'5px 0', display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:16 }}>{fIcon(sec.name)}</span>
                        {canDownload
                          ? <a href={sec.url} target="_blank" rel="noreferrer" style={{ color:pi.color||'#1a3a8a', textDecoration:'underline' }}>{sec.name||'attachment'}</a>
                          : <span style={{ color:'#bbb' }}>{sec.name||'attachment'} (downloads disabled)</span>
                        }
                        {sec.size ? <span style={{ color:'#999', fontSize:'9pt' }}>({fmtSize(sec.size)})</span> : null}
                      </div>
                    ) : null;
                  default: return null;
                }
              })}

              <div style={{ marginTop:32, borderTop:'1px solid #ccc', paddingTop:8, fontFamily:'Arial,sans-serif', fontSize:'8pt', color:'#aaa', display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:4 }}>
                <span>Spacemancraft Pvt Ltd · {pi.label} — Shared Document</span>
                <span>{fmtDate(post.uploadedAt)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Files tab */}
        {tab === 'files' && (
          <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
            <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim, letterSpacing:2, marginBottom:14 }}>FILES & ATTACHMENTS</div>
            {!canDownload && (
              <div style={{ background:`${SH.red}10`, border:`1px solid ${SH.red}44`, padding:'10px 14px',
                fontFamily:SH.mono, fontSize:10, color:SH.red, marginBottom:16 }}>
                🔒 The author has disabled downloads for this report. Contact the author or admin to enable.
              </div>
            )}
            {attachments.length === 0 ? (
              <div style={{ fontFamily:SH.mono, fontSize:12, color:SH.dim, textAlign:'center', padding:'48px 0' }}>No files in this report</div>
            ) : attachments.map((f, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 0', borderBottom:`1px solid ${SH.border}30` }}>
                <span style={{ fontSize:20, flexShrink:0 }}>{fIcon(f.name)}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:SH.mono, fontSize:11, color:SH.bright, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name||'file'}</div>
                  {f.size ? <div style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim }}>{fmtSize(f.size)}</div> : null}
                </div>
                {f.url && canDownload && (
                  <a href={f.url} download={f.name} target="_blank" rel="noreferrer"
                    style={{ fontFamily:SH.mono, fontSize:9, color:pi.color||'#888', textDecoration:'none',
                      border:`1px solid ${pi.color||'#888'}44`, padding:'5px 12px', whiteSpace:'nowrap', flexShrink:0 }}>
                    ↓ DOWNLOAD
                  </a>
                )}
                {f.url && !canDownload && (
                  <span style={{ fontFamily:SH.mono, fontSize:9, color:SH.dim, padding:'5px 12px' }}>🔒 LOCKED</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
